import { useEffect, useMemo } from "react";
import { ApolloProvider } from "@apollo/client";
import { RouterProvider } from "react-router-dom";
import { createApolloClient, expireProfileToken } from "@onmo/graphql";
import {
  selectIsAuthenticated,
  selectAuthReady,
  selectIsOnboarded,
  selectSigningIn,
} from "@onmo/auth";
import { routeDefs } from "./routes/Routes";
import { useAppSelector } from "./components/hooks";
import { isQRCodeLoginEnabled, isAndroidApp } from "./utils/platform";
import { LoadingScreen } from "./components/common/LoadingScreen/LoadingScreen";
import { setActivationCode } from "./models/storage";
import { getRoutePath, getRouteSearch } from "./models/location";
import { forceLogout } from "./models/auth";
import { initWakeLock } from "./utils/wakeLock";
import { prefetchGuideVideos } from "./models/guideVideos";
import { useSentryUser } from "./hooks/useSentryUser";
import { useAppLanguage } from "./hooks/useAppLanguage";
import * as Sentry from "@sentry/react";
import { loadIntercom } from "./utils/intercom";
import { useCookieConsent } from "./hooks/useCookieConsent";

const SentryUser = () => {
  useSentryUser();
  return null;
};

const AppLanguage = () => {
  useAppLanguage();
  return null;
};

// RouterProvider seeds its render state from `router` on mount only
// (useState(router.state)), and a loader-less router never emits again — so a
// swapped route table only reaches the screen if the provider remounts. The
// caller's `key` does that; the memo keeps the router stable in between.
const AppRoutes = ({
  isAuthenticated,
  isOnboarded,
}: {
  isAuthenticated: boolean;
  isOnboarded: boolean;
}) => {
  const router = useMemo(
    () => routeDefs(isAuthenticated, isOnboarded),
    [isAuthenticated, isOnboarded],
  );
  return <RouterProvider router={router} />;
};

export function App() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isOnboarded = useAppSelector(selectIsOnboarded);
  const authReady = useAppSelector(selectAuthReady);
  const signingIn = useAppSelector(selectSigningIn);
  const { isFunctionalAllowed } = useCookieConsent();
  const apolloClient = useMemo(() => {
    return createApolloClient({
      onGraphQLError: (err, operation) => {
        Sentry.captureException(new Error(`[GraphQL] ${err.message}`), {
          tags: { type: "graphql", operationName: operation.operationName },
          extra: {
            path: err.path,
            code: err.extensions?.code,
          },
        });
      },
      onCrossTenantAuthFailed: forceLogout,
      onProfileTokenInvalid: expireProfileToken,
    });
    // Need authReady because we need to get the token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady]);

  useEffect(() => {
    if (isFunctionalAllowed) loadIntercom();
  }, [isFunctionalAllowed]);

  useEffect(() => {
    initWakeLock();
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", handler);
    return () => document.removeEventListener("gesturestart", handler);
  }, []);

  useEffect(() => {
    if (getRoutePath() === "/activate") {
      const params = new URLSearchParams(getRouteSearch());
      const codeParams = params.get("code");
      if (codeParams) {
        setActivationCode(codeParams);
      }
    }
  }, []);

  // prefetch guide vides on first open
  useEffect(() => {
    prefetchGuideVideos(apolloClient);
  }, [apolloClient]);

  if (isAndroidApp && signingIn) return <LoadingScreen />;

  // For non-QR code login builds, wait for Keycloak
  if (!isQRCodeLoginEnabled && !authReady) return <LoadingScreen />;

  return (
    <ApolloProvider client={apolloClient}>
      <SentryUser />
      <AppLanguage />
      <AppRoutes
        key={`${isAuthenticated}-${isOnboarded}`}
        isAuthenticated={isAuthenticated}
        isOnboarded={isOnboarded}
      />
    </ApolloProvider>
  );
}

export default App;
